import { useQuery } from "@tanstack/react-query";
import { api, ApiError, type FieldChange, type ItemSummary, type PublishPreview } from "../api.js";
import {
  AlertIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  Hint,
  SendIcon,
  WithIcon
} from "../icons.js";
import { Modal } from "../modal.js";

/**
 * Confirm before publishing, showing exactly what callers will experience.
 *
 * Publishing is the one action here that reaches customers immediately and can only be
 * undone by rolling back, so it should be the most explained — it used to be a single
 * unguarded click.
 *
 * The diff is computed by the service, beside the projection logic, so this preview cannot
 * disagree with what publishing actually does.
 */
export function PublishDialog({
  restaurantId,
  onConfirm,
  onCancel,
  publishing,
  error
}: {
  restaurantId: string;
  onConfirm: () => void;
  onCancel: () => void;
  publishing: boolean;
  error?: unknown;
}) {
  const preview = useQuery({
    queryKey: ["publish-preview", restaurantId],
    queryFn: () => api.publishPreview(restaurantId),
    // Always re-check against live on open; a stale diff would be worse than none.
    staleTime: 0,
    gcTime: 0
  });

  const p = preview.data;
  const blocked = (p?.problems.length ?? 0) > 0;
  const nothingToDo = Boolean(p && !p.diff.hasChanges);

  return (
    <Modal label="Publish menu" wide onClose={onCancel}>
        <h2>Publish menu</h2>

        {preview.isLoading ? <p className="muted">Working out what will change…</p> : null}
        {preview.error ? (
          <div className="notice error">
            Couldn’t check what would change:{" "}
            {preview.error instanceof ApiError ? preview.error.message : String(preview.error)}
          </div>
        ) : null}

        {p ? (
          <>
            <p className="muted">
              {p.diff.isFirstPublish ? "First publish: the whole menu goes live." : `Live: v${p.liveVersion}`}
              <Hint>Takes effect on the next call.</Hint>
            </p>

            {blocked ? (
              <div className="notice error">
                <strong>
                  <WithIcon icon={<AlertIcon size={16} />}>Can’t publish yet</WithIcon>
                </strong>
                <ul>
                  {p.problems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {nothingToDo ? (
              <div className="notice">
                <WithIcon icon={<CheckCircleIcon size={16} />}>No changes since v{p.liveVersion}</WithIcon>
              </div>
            ) : (
              <div className="diff">
                {/* Callers losing a dish is the most consequential change, so it leads. */}
                <Group
                  title="Will no longer be offered"
                  tone="warn"
                  items={p.diff.noLongerOffered.map((i) => `${i.name} — ${i.price}`)}
                />
                <Group
                  title="Removed from the menu"
                  tone="warn"
                  items={p.diff.items.removed.map((i) => `${i.name} — ${i.price}`)}
                />
                <Group
                  title="New dishes"
                  tone="add"
                  items={p.diff.items.added.map((i) => `${i.name} — ${i.price}`)}
                />
                <Group
                  title="Offered again"
                  tone="add"
                  items={p.diff.offeredAgain.map((i) => `${i.name} — ${i.price}`)}
                />

                {p.diff.items.changed.length > 0 ? (
                  <section className="diff-group">
                    <h3>Changed dishes</h3>
                    <ul>
                      {p.diff.items.changed.map((c) => (
                        <li key={c.itemId}>
                          <strong>{c.name}</strong>
                          <ul className="changes">
                            {c.changes.map((f) => (
                              <li key={f.field}>
                                <Change {...f} />
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}

                {p.diff.groups.changed.length > 0 ? (
                  <section className="diff-group">
                    <h3>Changed options</h3>
                    <ul>
                      {p.diff.groups.changed.map((g) => (
                        <li key={g.groupId}>
                          <strong>{g.name}</strong>
                          {/* A group is shared: naming the dishes shows the blast radius. */}
                          {g.affects.length > 0 ? (
                            <span className="muted">
                              {" "}
                              — affects {g.affects.length} dish
                              {g.affects.length === 1 ? "" : "es"}: {g.affects.join(", ")}
                            </span>
                          ) : null}
                          <ul className="changes">
                            {g.changes.map((f, i) => (
                              <li key={`${f.field}-${i}`}>
                                <Change {...f} />
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}

                <Group title="New options" tone="add" items={p.diff.groups.added} />
                <Group title="Options removed" tone="warn" items={p.diff.groups.removed} />
                <Group title="New sections" tone="add" items={p.diff.categories.added} />
                <Group title="Sections removed" tone="warn" items={p.diff.categories.removed} />
                {p.diff.categories.renamed.length > 0 ? (
                  <section className="diff-group">
                    <h3>Renamed sections</h3>
                    <ul>
                      {p.diff.categories.renamed.map((f) => (
                        <li key={f.field}>
                          <Change {...f} field="" />
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}
                {p.diff.categories.reordered ? (
                  <section className="diff-group">
                    <h3>Sections reordered</h3>
                  </section>
                ) : null}
              </div>
            )}
          </>
        ) : null}

        {error ? (
          <div className="notice error">
            {error instanceof ApiError ? error.message : String(error)}
          </div>
        ) : null}

        <div className="modal-actions">
          <button className="ghost" onClick={onCancel} disabled={publishing}>
            Cancel
          </button>
          <button
            className="primary"
            onClick={onConfirm}
            disabled={publishing || preview.isLoading || blocked || nothingToDo}
          >
            <WithIcon icon={<SendIcon size={16} />}>{publishing ? "Publishing…" : "Publish"}</WithIcon>
          </button>
        </div>
    </Modal>
  );
}

function Group({
  title,
  items,
  tone
}: {
  title: string;
  items: string[];
  tone: "add" | "warn";
}) {
  if (items.length === 0) return null;
  return (
    <section className={`diff-group ${tone}`}>
      <h3>
        {title} <span className="count">{items.length}</span>
      </h3>
      <ul>
        {items.map((i) => (
          <li key={i}>{i}</li>
        ))}
      </ul>
    </section>
  );
}

function Change({ field, from, to }: FieldChange) {
  return (
    <>
      {field ? <span className="muted">{field}: </span> : null}
      <span className="was">{from}</span>
      <ArrowRightIcon size={14} className="arrow" />
      <span className="now">{to}</span>
    </>
  );
}

export type { ItemSummary, PublishPreview };
